import { CheckCircle2, Circle, Loader2 } from "lucide-react";

/**
 * Clean vertical phase tracker for the Client Portal.
 * Stages render top-to-bottom. Completed = filled green with check,
 * current = ring + spinner, upcoming = outline + grey.
 */
export default function VerticalPhaseTracker({ phases = [], projectStatus, testid = "vertical-phase-tracker" }) {
  if (!Array.isArray(phases) || phases.length === 0) {
    return (
      <div data-testid={`${testid}-empty`} className="text-sm text-neutral-500">
        Your studio hasn't published phases for this project yet.
      </div>
    );
  }
  const today = new Date().toISOString().slice(0, 10);
  const total = phases.length;
  const isCompleted = projectStatus === "Completed";

  let activeIdx = phases.findIndex((p) => (!p.start_date || p.start_date <= today) && (!p.end_date || p.end_date >= today));
  if (activeIdx === -1) {
    const pastCount = phases.filter((p) => p.end_date && p.end_date < today).length;
    activeIdx = pastCount >= total ? total - 1 : pastCount;
  }

  return (
    <div data-testid={testid} className="bg-white border border-black/5 rounded-[1.25rem] p-6">
      <div className="label-caps text-royal-800 mb-5">Project progress</div>
      <ol className="relative">
        {phases.map((p, i) => {
          const done = i < activeIdx || isCompleted;
          const current = !isCompleted && i === activeIdx;
          const upcoming = !done && !current;
          const isLast = i === total - 1;
          const stateLabel = done ? "Completed" : current ? "In progress" : "Upcoming";
          return (
            <li key={p.id || i} data-testid={`${testid}-phase-${i}`} className="relative pl-14 pb-8 last:pb-0">
              {!isLast && (
                <span
                  aria-hidden
                  className={`absolute left-[18px] top-10 bottom-0 w-0.5 ${done ? "bg-[#1B4332]" : "bg-neutral-200"}`}
                />
              )}
              <div
                className={`absolute left-0 top-1 w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                  done
                    ? "bg-[#1B4332] border-[#1B4332] text-white"
                    : current
                      ? "bg-white border-royal-700 text-royal-700 ring-4 ring-royal-700/15"
                      : "bg-white border-neutral-300 text-neutral-400"
                }`}
              >
                {done ? <CheckCircle2 size={18} /> : current ? <Loader2 size={16} className="animate-spin" /> : <Circle size={10} className="fill-current" />}
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={`font-display text-lg leading-tight ${done ? "text-[#1B4332]" : current ? "text-royal-800" : "text-ink/70"}`}>
                    {p.name}
                  </div>
                  {(p.start_date || p.end_date) && (
                    <div className="text-xs text-ink/50 font-mono-tight mt-0.5">
                      {p.start_date || "—"} → {p.end_date || "—"}
                    </div>
                  )}
                </div>
                <span
                  data-testid={`${testid}-phase-${i}-state`}
                  className={`shrink-0 text-[10px] font-mono-tight uppercase tracking-widest px-2 py-1 rounded-full ${
                    done
                      ? "bg-[#E9F5EE] text-[#175E33]"
                      : current
                        ? "bg-royal-700/10 text-royal-800"
                        : "bg-neutral-100 text-neutral-500"
                  }`}
                >
                  {stateLabel}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
